import React, { useLayoutEffect, useRef, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import moment from 'moment';

import CircularProgress from '@material-ui/core/CircularProgress';
import Download from '@material-ui/icons/GetApp';
import makeStyles from '@material-ui/core/styles/makeStyles';

import ActionButton from 'components/buttons/ActionButton';

const useStyles = makeStyles((theme) => ({
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
}));

const ExportDebtorsButton = (props) => {
  const classes = useStyles();

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [loading, setLoading] = useState<boolean>(false);
  const mountedRef = useRef(false);
  const runningRef = useRef(false);
  const operationRef = useRef(0);
  const timeoutRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      runningRef.current = false;
      operationRef.current += 1;
      if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    };
  }, []);

  const handleExportDebtors = async () => {
    if (!mountedRef.current || runningRef.current) return;
    runningRef.current = true;
    const operation = ++operationRef.current;
    const isCurrent = () => mountedRef.current && operationRef.current === operation;
    const finish = () => {
      if (!isCurrent()) return;
      runningRef.current = false;
      setLoading(false);
    };
    setLoading(true);

    const poll = async (executionArn: string) => {
      if (!isCurrent()) return;
      timeoutRef.current = null;
      try {
        const {
          data: { status, downloadURL },
        } = await dataProvider.get('exportReportStatus', {
          arn: executionArn,
        });
        if (!isCurrent()) return;
        if (status === 'RUNNING') {
          timeoutRef.current = window.setTimeout(() => poll(executionArn), 5000);
          return;
        }
        if (status === 'SUCCEEDED' && downloadURL) {
          const response = await fetch(downloadURL);
          if (!isCurrent()) return;
          if (!response.ok) throw new Error(`Failed to download report (${response.status}).`);
          const text = await response.text();
          if (!isCurrent()) return;
          const url = window.URL.createObjectURL(new Blob([text]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `Debtors_${moment().format()}.csv`);
          document.body.appendChild(link);
          try {
            link.click();
          } finally {
            link.remove();
            window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
          }
        }
        finish();
      } catch (error) {
        if (!isCurrent()) return;
        finish();
        notify(error?.message || 'Failed to export debtors');
      }
    };

    try {
      const {
        data: { executionArn },
      } = await dataProvider.get('exportDebtors');
      if (!isCurrent()) return;
      timeoutRef.current = window.setTimeout(() => poll(executionArn), 5000);
    } catch (error) {
      if (!isCurrent()) return;
      finish();
      notify(error?.message || 'Failed to export debtors');
    }
  };

  return (
    <ActionButton
      disabled={loading}
      onClick={handleExportDebtors}
      classes={{
        label: classes.button,
      }}>
      Export Debtors {loading ? <CircularProgress size={20} /> : <Download />}
    </ActionButton>
  );
};

export default ExportDebtorsButton;
